/**
 * TabsControl
 * Renders a tabbed layout container.
 * Expects control.children[] with each child having a label property.
 * Performance: Memoized to prevent unnecessary re-renders, useCallback for tab click handler.
 */

import React, { useState, useCallback, memo } from 'react';
import type { FormControl } from '@atlas-forms/types-js';

export interface TabsControlProps {
  control: FormControl & { children?: FormControl[] };
  renderChild: (child: FormControl) => React.ReactNode;
}

export const TabsControl = memo(({ control, renderChild }: TabsControlProps) => {
  const tabs = control.children ?? [];
  const [activeIndex, setActiveIndex] = useState(0);

  const handleTabClick = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  if (tabs.length === 0) return null;

  return (
    <div className="af-tabs-control" data-control-id={control.id}>
      <div className="af-tabs-control__tab-list" role="tablist">
        {tabs.map((tab, i) => (
          <button
            key={tab.id ?? i}
            role="tab"
            aria-selected={i === activeIndex}
            aria-controls={`tab-panel-${control.id}-${i}`}
            onClick={() => handleTabClick(i)}
            className={`af-tabs-control__tab ${i === activeIndex ? 'af-tabs-control__tab--active' : ''}`}
          >
            {tab.label ?? `Tab ${i + 1}`}
          </button>
        ))}
      </div>
      {tabs.map((tab, i) => (
        <div
          key={tab.id ?? i}
          role="tabpanel"
          id={`tab-panel-${control.id}-${i}`}
          hidden={i !== activeIndex}
          className="af-tabs-control__panel"
        >
          {renderChild(tab)}
        </div>
      ))}
    </div>
  );
});

TabsControl.displayName = 'TabsControl';
